import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from "recharts";
import "./DataBarChart.css";
import useInterval from "../../../hooks/UseInterval";

const DataBarChart = ({
  chartData,
  title,
  category,
  width,
  height,
  isAutomated,
  isDisabled,
  handleFilter,
  timeInterval,
  refetch,
  fontSize,
  margin
}) => {
  const [selectedValue, setSelectedValue] = useState("today");
  const [xAxisLabel, setXAxisLabel] = useState("xValue");
  const [data, setData] = useState(chartData);

  useEffect(() => setData(chartData), [chartData]);
  useEffect(() => {
    if (category === "individual") {
      setSelectedValue("month");
    }
  }, []);

  const handleRadioChange = (event) => {
    setSelectedValue(event.target.value);
    dataSetter(event.target.value);
  };

  function dataSetter(value) {
    switch (value) {
      case "today":
        handleFilter("today");
        setXAxisLabel("xValue");
        setData(data);
        break;
      case "week":
        handleFilter("week");
        setXAxisLabel("xValue");
        setData(data);
        break;
      case "month":
        handleFilter("month");
        setXAxisLabel("xValue");
        setData(data);
        break;
      case "year":
        handleFilter("year");
        setXAxisLabel("xValue");
        setData(data);
        break;
      default:
        setData(data);
    }
  }

  useInterval(() => {
    refetch();
  }, 30000);

  useEffect(() => {
    if (isAutomated) {
      setSelectedValue(timeInterval);
      dataSetter(timeInterval);
    }
  }, [selectedValue, isAutomated]);

  function currencyFormatter(value) {
    const formattedValue = value.toLocaleString("en-GB", {
      style: "currency",
      currency: "GBP",
    });
    return formattedValue.substring(0, formattedValue.indexOf("."));
  }

  function getTotalSales() {
    const totalPrice = data.reduce((sum, item) => sum + item.achievement, 0);
    return totalPrice;
  }

  function getTotalTarget() {
    const totalPrice = data.reduce((sum, item) => sum + item.target, 0);
    return totalPrice;
  }

  const getColor = (value, target, breakEven) => {
    if (value >= target) {
      return "url(#colorGreen)";
    } else if (value < target && value > breakEven) {
      return "url(#colorYellow)";
    } else {
      return "url(#colorRed)";
    }
  };
  return (
    <div>
      <div className="row mb-3">
        <div className="col-7">
          <h3 className="chart-title">{title}</h3>
          {category === "individual"  ? (
            <div
              className={`date-filter date-filter-dashboard  ${
                category === "bookedOrdersBar" ? "max-width" : ""
              }`}
              role="group"
              aria-label="Basic radio toggle button group"
            >
              <input
                type="radio"
                className="btn-check"
                name={category}
                id={category + "3"}
                value="month"
                checked={selectedValue === "month"}
                onChange={handleRadioChange}
                disabled={isDisabled}
              />
              <label
                className={`btn ${selectedValue === "month" ? "checked" : ""}`}
                htmlFor={category + "3"}
              >
                {category != "bookedOrdersBar" ? "Month" : "Month"}
              </label>
            </div>
          ) : category === "company" ? (
            <div
              className={`date-filter date-filter-dashboard  ${
                category === "bookedOrdersBar" ? "max-width" : ""
              }`}
              role="group"
              aria-label="Basic radio toggle button group"
            >
              <input
                type="radio"
                className="btn-check"
                name={category}
                id={category + "1"}
                value="today"
                checked={selectedValue === "today"}
                onChange={handleRadioChange}
                disabled={isDisabled}
              />
              <label
                className={`btn ${selectedValue === "today" ? "checked" : ""}`}
                htmlFor={category + "1"}
              >
                Today
              </label>
            </div>
          ) : (
            <div
              className={`date-filter ${
                category === "bookedOrdersBar" ? "" : ""
              }`}
              role="group"
              aria-label="Basic radio toggle button group"
            >
              <input
                type="radio"
                className="btn-check"
                name={category}
                id={category + "1"}
                value="today"
                checked={selectedValue === "today"}
                onChange={handleRadioChange}
                disabled={isDisabled}
              />
              <label
                className={`btn ${selectedValue === "today" ? "checked" : ""}`}
                htmlFor={category + "1"}
              >
                Today
              </label>

              <input
                type="radio"
                className="btn-check"
                name={category}
                id={category + "2"}
                value="week"
                checked={selectedValue === "week"}
                onChange={handleRadioChange}
                disabled={isDisabled}
              />
              <label
                className={`btn ${selectedValue === "week" ? "checked" : ""}`}
                htmlFor={category + "2"}
              >
                Week
              </label>
              <input
                type="radio"
                className="btn-check"
                name={category}
                id={category + "3"}
                value="month"
                checked={selectedValue === "month"}
                onChange={handleRadioChange}
                disabled={isDisabled}
              />
              <label
                className={`btn ${selectedValue === "month" ? "checked" : ""}`}
                htmlFor={category + "3"}
              >
                {category != "bookedOrdersBar" ? "Month" : "Month"}
              </label>
              {/* <input
                type="radio"
                className="btn-check"
                name={category}
                id={category + "4"}
                value="year"
                checked={selectedValue === "year"}
                onChange={handleRadioChange}
                disabled={isDisabled}
              />
              <label
                className={`btn ${selectedValue === "year" ? "checked" : ""}`}
                htmlFor={category + "4"}
              >
                {category != "bookedOrdersBar" ? "Year" : "Yearly"}
              </label> */}
            </div>
          )}

        </div>
        <div className="col-5">
          <div className="stats">
            <small>Total {title} Achieved</small>
            <br />
            <span className="h5 mb-0 bolder-text sales-achievement">
              {currencyFormatter(getTotalSales())}
              <small className="h6 fw-bold">
                /{currencyFormatter(getTotalTarget())}
              </small>
            </span>
            {getTotalSales() > getTotalTarget() ? (
              <>
                <h5 className="sales-stats success">
                  +{currencyFormatter(getTotalSales() - getTotalTarget())}
                </h5>
                <small className="sales-stats success status-warning">
                  Target Surpassed
                </small>
              </>
            ) : (
              <>
                <h5 className="sales-stats danger">
                  -{currencyFormatter(getTotalTarget() - getTotalSales())}
                </h5>
                <small className="sales-stats danger status-warning">
                  To Go
                </small>
              </>
            )}
          </div>
        </div>
      </div>
      <BarChart
        width={width}
        height={height}
        data={data}
        margin={{
          top: 5,
          right: 30,
          left: margin,
          bottom: 5,
        }}
      >
        <defs>
          <linearGradient id="colorGreen" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#5EC682" stopOpacity={1} />
            <stop offset="95%" stopColor="#9EDDB6" stopOpacity={0.8} />
          </linearGradient>
          <linearGradient id="colorRed" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#E84C4C" stopOpacity={1} />
            <stop offset="95%" stopColor="#FDA2A2" stopOpacity={0.8} />
          </linearGradient>
          <linearGradient id="colorYellow" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#E8B84C" stopOpacity={1} />
            <stop offset="95%" stopColor="#FDEFA2" stopOpacity={0.8} />
          </linearGradient>
          <linearGradient id="colorGrey" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#E0E0E0" stopOpacity={1} />
            <stop offset="95%" stopColor="#F0F0F0" stopOpacity={1} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey={xAxisLabel} color="#19A500" fontSize={fontSize} fontWeight={900} />
        <YAxis color="#19A500" fontSize={fontSize} fontWeight={900} tickFormatter={(value) => `${currencyFormatter(value)}`} />
        <Tooltip formatter={(value) => currencyFormatter(value)} />
        <Bar dataKey="achievement" fill="url(#color)" stackId="a">
          {data.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={getColor(entry.achievement, entry.target, entry.breakEven)}
            />
          ))}
        </Bar>
        {data.filter((entry) => entry.target > entry.achievement).length >
          0 && (
          <Bar
            dataKey={(data) =>
              data.target - data.achievement > 0
                ? data.target - data.achievement
                : 0
            }
            stackId="a"
            fill="url(#colorGrey)"
          />
        )}
      </BarChart>
    </div>
  );
};

export default DataBarChart;
